import { Box, Button, Typography } from "@mui/material";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useUpdateAdvisor } from "services/advisor";
import { UpdateAdvisorRequest } from "protogen/advisors_service_pb";
import LinkRouter from "../navigation/LinkRouter";
import { useSnackpack } from "../context/SnackpackContextProvider";
import useIsMobile from "../hooks/useIsMobile";

const FOURTEEN_DAYS_IN_SECONDS = 14 * 24 * 60 * 60;

export const shouldShowClientOpportunitiesAlert = (
  newClientOpportunitiesToggledSec: number,
): boolean => {
  if (newClientOpportunitiesToggledSec === 0) {
    return false;
  }
  const currentTimeSec = Math.floor(Date.now() / 1000);
  return (
    currentTimeSec - newClientOpportunitiesToggledSec > FOURTEEN_DAYS_IN_SECONDS
  );
};

interface ClientOpportunitiesAlertProps {
  advisorRef: string;
  currentValue: boolean;
}

export default ({ advisorRef }: ClientOpportunitiesAlertProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const { pushSnack } = useSnackpack();
  const isMobile = useIsMobile();
  const { request: updateRequest } = useUpdateAdvisor(() => {
    setIsUpdating(false);
  });

  const handleToggle = async (newValue: boolean) => {
    setIsUpdating(true);
    await updateRequest(
      new UpdateAdvisorRequest({
        advisorRef: advisorRef,
        shouldUpdateNewClientOpportunitiesEnabled: true,
        newClientOpportunitiesEnabled: newValue,
        shouldUpdateSpecialties: false,
      }),
    );

    if (newValue) {
      pushSnack("You'll keep receiving new client opportunities");
    } else {
      pushSnack("We've turned off new client opportunities");
    }

    // Collapse after 3 seconds
    setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        margin: "16px 0",
        padding: "20px 24px",
        backgroundColor: "#F6F9F9",
        borderRadius: "12px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "space-between",
        gap: isMobile ? "16px" : "8px",
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: "-0.32px",
            color: "#3D3D3D",
          }}
        >
          Are you open to new client opportunities?
        </Typography>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            lineHeight: 1.41,
            letterSpacing: "-0.15px",
            color: "#3D3D3D",
          }}
        >
          Receive new client offers based on your{" "}
          <LinkRouter
            inline={true}
            to="/forms/complete/3e07784a-f3fb-412e-aefb-d9be4a4677ec"
            sx={{
              fontWeight: 600,
              color: "#198282",
              textDecoration: "none",
            }}
          >
            matchmaking details
          </LinkRouter>
          .
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => handleToggle(true)}
          disabled={isUpdating}
          startIcon={<Check size={20} />}
          sx={{
            padding: "12px",
            borderRadius: "8px",
            borderColor: "#D4D4D4",
            color: "#3D3D3D",
            fontSize: "15px",
            fontWeight: 500,
            lineHeight: 1.41,
            letterSpacing: "-0.15px",
            textTransform: "none",
            minWidth: "auto",
            gap: "8px",
            backgroundColor: "transparent",
            flex: isMobile ? 1 : "unset",
            "&:hover": {
              backgroundColor: "#F6F9F9",
              borderColor: "#D4D4D4",
            },
            "& .MuiButton-startIcon": {
              margin: 0,
            },
          }}
        >
          Yes
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleToggle(false)}
          disabled={isUpdating}
          startIcon={<X size={20} />}
          sx={{
            padding: "12px",
            borderRadius: "8px",
            borderColor: "#D4D4D4",
            color: "#3D3D3D",
            fontSize: "15px",
            fontWeight: 500,
            lineHeight: 1.41,
            letterSpacing: "-0.15px",
            textTransform: "none",
            minWidth: "auto",
            gap: "8px",
            backgroundColor: "transparent",
            flex: isMobile ? 1 : "unset",
            "&:hover": {
              backgroundColor: "#F6F9F9",
              borderColor: "#D4D4D4",
            },
            "& .MuiButton-startIcon": {
              margin: 0,
            },
          }}
        >
          No
        </Button>
      </Box>
    </Box>
  );
};
